import { type PublishPriceCheckResponse } from '@utazzo/shared';
import { UzButton } from '@utazzo/ui';
import { TrendingDown, TrendingUp } from 'lucide-react';
import React, { useEffect } from 'react';

import { fmtMoney } from './price-visibility';

/** Confirm popup when live prices moved before publish. */
export const PublishPriceCheckModal = ({
  check,
  confirming,
  error,
  onConfirm,
  repriceBlocked = false,
  onRepriceAgain,
}: {
  check: PublishPriceCheckResponse;
  confirming: boolean;
  error: string | null;
  onConfirm: () => void;
  /**
   * True when an active reprice is in force and live prices moved. The solved
   * target no longer holds, so publishing is blocked - the operator must
   * reprice again rather than confirm the stale total.
   */
  repriceBlocked?: boolean;
  /** Close the modal and take the operator back to the Pricing step to reprice. */
  onRepriceAgain?: () => void;
}): React.JSX.Element => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const currency = check.currencyCode || 'INR';
  const changedLines = check.components.filter((c) => c.appliedCostPaise !== c.previousCostPaise);
  const netDelta = check.changeByPaise ? BigInt(check.changeByPaise) : 0n;
  const increased = netDelta > 0n;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Price update before publish"
    >
      <div className="max-h-[85vh] w-full max-w-xl overflow-y-auto rounded-xl bg-white shadow-2xl">
        <div className="sticky top-0 border-b border-zinc-200 bg-white p-6">
          <div className="flex items-center gap-3">
            <span
              className={`flex h-10 w-10 items-center justify-center rounded-full ${
                increased ? 'bg-emerald-100' : 'bg-red-100'
              }`}
            >
              {increased ? (
                <TrendingUp className="h-5 w-5 text-emerald-600" aria-hidden />
              ) : (
                <TrendingDown className="h-5 w-5 text-red-600" aria-hidden />
              )}
            </span>
            <div>
              <p className="text-lg font-semibold text-zinc-900">Prices Have Changed</p>
              <p className="text-sm text-zinc-500">
                Supplier rates moved since this version was copied or last saved.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4 p-6">
          <div className={`rounded-lg p-4 ${increased ? 'bg-emerald-50' : 'bg-red-50'}`}>
            <p className="text-sm text-zinc-800">
              The quote total will {increased ? 'increase' : 'decrease'} by{' '}
              <span className="font-semibold">
                {fmtMoney(String(netDelta < 0n ? -netDelta : netDelta), currency)}
              </span>
              .
            </p>
            <p className="mt-1 text-sm text-zinc-600">
              New total:{' '}
              <span className="font-semibold">
                {fmtMoney(check.newGrandTotalPaise ?? check.currentGrandTotalPaise, currency)}
              </span>
            </p>
          </div>

          {changedLines.length > 0 && (
            <ul className="divide-y divide-zinc-100 rounded-lg border border-zinc-200">
              {changedLines.map((c) => {
                const delta = BigInt(c.appliedCostPaise) - BigInt(c.previousCostPaise);
                const up = delta > 0n;
                return (
                  <li key={c.componentId} className="flex items-center justify-between gap-4 p-4">
                    <p className="text-sm font-medium text-zinc-900">
                      {c.kind === 'FLIGHT' ? 'Flight' : 'Hotel'} · {c.title}
                    </p>
                    <span
                      className={`shrink-0 text-sm font-semibold tabular-nums ${
                        up ? 'text-emerald-600' : 'text-red-600'
                      }`}
                    >
                      {up ? '+' : '-'}
                      {fmtMoney(String(up ? delta : -delta), currency)}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}

          {repriceBlocked ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm font-semibold text-amber-900">Reprice no longer valid</p>
              <p className="mt-1 text-sm text-amber-800">
                This itinerary was repriced to an exact total, but supplier prices have changed
                since then. Publishing is blocked - please reprice again to the target you want,
                then publish.
              </p>
            </div>
          ) : (
            <p className="text-xs text-zinc-500">
              Confirming applies the latest live prices, then publishes this version with an updated
              share link.
            </p>
          )}

          {error && <p className="text-sm font-medium text-red-600">{error}</p>}

          <div className="flex justify-end pt-2">
            {repriceBlocked ? (
              <UzButton type="button" onClick={onRepriceAgain}>
                Reprice again
              </UzButton>
            ) : (
              <UzButton type="button" onClick={onConfirm} loading={confirming}>
                {confirming ? 'Publishing…' : 'Confirm & Publish'}
              </UzButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
